import { Plus, Upload, ArrowRight, AlertCircle } from 'lucide-react'
import { useWizardAdjustmentsController } from '@/hooks/useWizardAdjustmentsController'
import { DriverAdjustmentsTable } from './DriverAdjustmentsTable'
import { AddSingleCompensationDialog } from '@/components/compensation/AddSingleCompensationDialog'
import { CompensationFileUploadDialog, CompensationFileUploadData } from '@/components/compensation/CompensationFileUploadDialog'
import { CompensationService } from '@/services/compensationService'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { SpinnerIcon } from '@/components/ui/SpinnerIcon'
import { toast } from 'sonner'

interface WizardAdjustmentsContentProps {
  periodId: string
  onComplete: () => void
}

export function WizardAdjustmentsContent({ periodId, onComplete }: WizardAdjustmentsContentProps) {
  const { currentUser } = useAuth()
  const controller = useWizardAdjustmentsController(periodId)
  const { state } = controller

  const handleBulkUploadComplete = async (data: CompensationFileUploadData[]) => {
    if (!currentUser || data.length === 0) return

    try {
      // For now, bulk upload creates compensations with default settings
      // User can edit them individually after import
      const compensations = data.map(item => ({
        driverId: item.driverId,
        vehicleId: item.vehicleId,
        type: 'compensation' as const,
        applyTo: 'net_earnings' as const,
        amount: item.amount,
        category: 'other_compensation' as const,
        comment: item.originalRowCount > 1
          ? `Importerad från fil (${item.originalRowCount} rader summerade)`
          : 'Importerad från fil'
      }))

      await CompensationService.createBulkCompensations(currentUser, {
        period: periodId,
        compensations
      })

      toast.success(`${data.length} ersättningar importerade`)
      controller.closeBulkUploadDialog()
      controller.reloadData()
    } catch (error) {
      console.error('Failed to import compensations:', error)
      toast.error('Kunde inte importera ersättningar')
    }
  }

  const handleCompensationSuccess = () => {
    controller.closeAddDialog()
    controller.reloadData()
  }

  const handleDeleteCompensation = async (compensationId: string) => {
    try {
      await controller.deleteCompensation(compensationId)
      toast.success('Ersättning/avdrag borttaget')
    } catch (error) {
      toast.error('Kunde inte ta bort ersättning/avdrag')
    }
  }

  const handleDeleteEarning = async (earningId: string) => {
    try {
      await controller.deleteEarning(earningId)
      toast.success('Intäkt borttagen')
    } catch (error) {
      toast.error('Kunde inte ta bort intäkt')
    }
  }

  const handleContinue = async () => {
    try {
      // Save exclusions before proceeding
      await controller.saveExclusions()
      onComplete()
    } catch (error) {
      toast.error('Kunde inte spara ändringar')
    }
  }

  // Loading state
  if (state.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <SpinnerIcon size="lg" className="text-blue-600" />
      </div>
    )
  }

  // Error state
  if (state.error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{state.error}</AlertDescription>
      </Alert>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold">Justeringar</h1>
        <p className="text-muted-foreground">
          Lägg till ersättningar och avdrag för perioden
        </p>
      </div>

      {/* Action bar */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => controller.openAddDialog()}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Lägg till
          </Button>
          <Button
            variant="outline"
            onClick={controller.openBulkUploadDialog}
            className="gap-2"
          >
            <Upload className="h-4 w-4" />
            Ladda upp fil
          </Button>
        </div>
        <Button onClick={handleContinue} className="gap-2">
          Fortsätt
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Driver table */}
      {state.driverSummaries.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <p>Inga förare med intäkter eller justeringar för denna period.</p>
          <p className="text-sm mt-2">
            Ladda upp intäktsdata i föregående steg först.
          </p>
        </div>
      ) : (
        <DriverAdjustmentsTable
          summaries={state.driverSummaries}
          expandedDriverIds={state.expandedDriverIds}
          excludedDriverIds={state.excludedDriverIds}
          onToggleExpand={controller.toggleDriverExpanded}
          onToggleExclusion={controller.toggleDriverExclusion}
          onAddCompensation={controller.openAddDialog}
          onEditCompensation={controller.openEditDialog}
          onDeleteCompensation={handleDeleteCompensation}
          onDeleteEarning={handleDeleteEarning}
        />
      )}

      {/* Add/Edit compensation dialog */}
      <AddSingleCompensationDialog
        open={state.showAddDialog}
        onOpenChange={(open) => {
          if (!open) controller.closeAddDialog()
        }}
        compensation={state.editingCompensation}
        prefilledDriverId={state.selectedDriverId || undefined}
        prefilledPeriod={periodId}
        drivers={state.drivers}
        onSuccess={handleCompensationSuccess}
      />

      {/* Bulk upload dialog */}
      <CompensationFileUploadDialog
        open={state.showBulkUploadDialog}
        onOpenChange={(open) => {
          if (!open) controller.closeBulkUploadDialog()
        }}
        onDataUploaded={handleBulkUploadComplete}
        targetType="driver"
        drivers={state.drivers}
        vehicles={[]}
      />
    </div>
  )
}
